import FlexibleGrid from "@/components/FlexibleGrid";
import ListItem from "@/features/list/components/ListItem";
import MakerContactModal from "@/features/maker_contact/components/MakerContactModal";
import MakerIndexHeader from "@/features/maker/components/MakerIndexHeader";
import PostItem from "@/features/post/components/PostItem";
import PostItemList from "@/features/post/components/PostItemList";
import ProductVariationItem from "@/features/product_variation/components/ProductVariationItem";
import ProductVariationItemList from "@/features/product_variation/components/ProductVariationItemList";
import SocialLinks from "@/components/SocialLinks";
import useDisclosure from "@/hooks/useDisclosure";
import useModalLimitFunction from "@/features/user/hooks/useModalLimitFunction";
import DefaultLayout from "@/layouts/DefaultLayout";
import client from "@/utils/api/client";
import { notFoundWrapper } from "@/utils/notFoundWrapper";
import publicPath from "@/utils/publicPath";
import {
  ListResponse,
  MakerIndexResponse,
  MakerSectionContentType,
  MakerSectionResponse,
  PostResponse,
  ProductKind,
  ProductVariationWithProductResponse,
} from "@/web-client/api";
import { RedirectReason } from "@/domain/values/RedirectReason";
import useAnalytics from "@/hooks/useAnalytics";
import useDevice from "@/hooks/useDevice";
import usePageView from "@/hooks/usePageView";
import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import { NextSeo } from "next-seo";
import Link from "next/link";
import { PropsWithChildren, useCallback, useMemo } from "react";
import useSWR, { KeyedMutator } from "swr";
import { useSignInRequiredAction } from "@/features/user_authentication";
import { getReasonText } from "@/utils/getReasonText";

type Props = {
  maker: MakerIndexResponse;
};

const MakerIndexPage: NextPage<Props> = ({ maker: initMaker }): JSX.Element => {
  usePageView(initMaker.id);
  const { isPc } = useDevice();
  const { data: maker, mutate } = useSWR<MakerIndexResponse>(
    `/makers/${initMaker.id}}`,
    async () => {
      const { data } = await client.makersIdGet({ id: Number(initMaker.id) });

      return data;
    },
    { fallbackData: initMaker },
  );

  const { event } = useAnalytics(maker.id);

  const clickHandler = useCallback(
    (linkType: string) => {
      event("click_button", {
        content_type: linkType,
        item_id: `M_${maker.id}`,
      });
    },
    [maker, event],
  );

  // コピーもしくは説明文がある場合は2カラムレイアウトになる（PCのみ）
  const isMultiColumnLayout = useMemo(
    () => maker.copy || maker.description,
    [maker],
  );

  return (
    <DefaultLayout>
      <NextSeo
        title={`${maker.name}`}
        description={`${maker.name} | ${maker.description}}`}
        openGraph={
          maker.logo ? { images: [{ url: maker.logo.urls.original }] } : {}
        }
        twitter={{
          cardType: "summary",
        }}
        canonical={publicPath(`/makers/${maker.id}`)}
      />
      <div className="pb-120">
        <MakerIndexHeader maker={maker} />
        <main className="px-16 space-y-80 max-w-[1280px] mx-auto laptop:py-64 pb-40">
          <section className="tracking-wider">
            {isMultiColumnLayout ? (
              <div className="grid gap-64 laptop:grid-cols-3 laptop:gap-32">
                <div className="laptop:col-start-1 laptop:col-end-3 pr-24 laptop:border-r laptop:border-primary space-y-32">
                  <div className="space-y-16 laptop:text-left text-center">
                    <h2 className="font-bold text-lg laptop:text-3xl whitespace-pre-line">
                      {maker.copy}
                    </h2>
                    <p className="leading-loose whitespace-pre-line break-all">
                      {maker.description}
                    </p>
                  </div>
                  <div className="flex small:justify-center">
                    <SocialLinks
                      contactableResponese={{
                        ...maker,
                        contact_email: undefined,
                      }}
                      clickHandler={clickHandler}
                    />
                  </div>
                </div>

                <div className="laptop:col-start-3 laptop:col-end-4 ">
                  {maker.categories && maker.categories.length > 0 && (
                    <CategoryArea
                      maker={maker}
                      align={isPc ? "left" : "center"}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-48">
                {(maker.copy || maker.description) && (
                  <div className="space-y-16 text-center">
                    {maker.copy && (
                      <h2 className="font-bold text-lg laptop:text-3xl whitespace-pre-line">
                        {maker.copy}
                      </h2>
                    )}
                    {maker.description && (
                      <p className="leading-loose whitespace-pre-line text-center max-w-[640px] mx-auto break-all">
                        {maker.description}
                      </p>
                    )}
                  </div>
                )}
                {maker.categories && maker.categories.length > 0 && (
                  <CategoryArea maker={maker} align="center" />
                )}
                <div className="flex justify-center">
                  <SocialLinks
                    contactableResponese={{
                      ...maker,
                      contact_email: undefined,
                    }}
                    clickHandler={clickHandler}
                  />
                </div>
              </div>
            )}
          </section>

          {maker.sections &&
            maker.sections.map((section) => (
              <MakerSection
                maker={maker}
                section={section}
                mutate={mutate}
                key={section.id}
              />
            ))}
        </main>
      </div>
    </DefaultLayout>
  );
};
export default MakerIndexPage;

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  return notFoundWrapper<Props>(async () => {
    const { data: maker } = await client.makersIdGet({ id: Number(params.id) });

    return {
      props: {
        maker,
      },
      revalidate: 60,
    };
  });
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: "blocking",
});

type MakerSectionWrapperProps = {
  title: string;
  href?: string;
  linkLabel?: string;
} & PropsWithChildren;
const MakerSectionWrapper: React.FC<MakerSectionWrapperProps> = ({
  title,
  href,
  children,
  linkLabel,
}): JSX.Element => {
  return (
    <section className="space-y-32 laptop:space-y-64">
      <div>
        <div
          className="w-full h-[1px]"
          style={{
            background: `linear-gradient(90deg, #000 0%, #000 6%, #d3d3d3 6%, #d3d3d3 100%)`,
          }}
        />
        <header className="flex justify-between items-center py-8">
          <h2 className="font-bold text-sm">{title}</h2>
          <div>
            {href && (
              <Link href={href} className="text-sm hover:underline">
                {linkLabel}
              </Link>
            )}
          </div>
        </header>
      </div>
      <div>{children}</div>
    </section>
  );
};

type MakerSectionProps = {
  maker: MakerIndexResponse;
  section: MakerSectionResponse;
  mutate: KeyedMutator<MakerIndexResponse>;
};
const MakerSection = ({
  section,
  maker,
  mutate,
}: MakerSectionProps): JSX.Element => {
  // アイテムがなければ基本的には表示しない
  if (section.content.items.length === 0) return <></>;

  if (
    section.content_type ===
      MakerSectionContentType.LATEST_PRODUCT_VARIATIONS ||
    section.content_type === MakerSectionContentType.PICKUP_PRODUCT_VARIATIONS
  ) {
    return (
      <MakerSectionWrapper
        title={section.title}
        href={`/makers/${maker.id}/${maker.building_material_product_variations_count >= maker.interior_product_variations_count ? "building_materials" : "interiors"}`}
        linkLabel="すべての製品"
      >
        <ProductVariationItemList
          productVariations={
            section.content.items as ProductVariationWithProductResponse[]
          }
          onRender={(variation) => (
            <ProductVariationItem
              productVariation={variation}
              mutate={() => mutate()}
            />
          )}
        />
      </MakerSectionWrapper>
    );
  }

  if (
    section.content_type === MakerSectionContentType.PICKUP_POSTS ||
    section.content_type === MakerSectionContentType.LATEST_POSTS
  ) {
    return (
      <MakerSectionWrapper
        title={section.title}
        href={`/makers/${maker.id}/posts`}
        linkLabel="すべての事例写真"
      >
        <PostItemList
          posts={section.content.items as PostResponse[]}
          onRender={(post) => (
            <PostItem key={post.id} post={post} mutate={() => mutate()} />
          )}
        />
      </MakerSectionWrapper>
    );
  }

  if (section.content_type === MakerSectionContentType.PICKUP_LISTS) {
    return (
      <MakerSectionWrapper
        title={section.title}
        href={`/makers/${maker.id}/lists`}
        linkLabel="すべてのリスト"
      >
        <FlexibleGrid>
          {(section.content.items as ListResponse[]).map((list) => {
            return (
              <li key={list.id}>
                <ListItem list={list} hasUserIcon={false} />
              </li>
            );
          })}
        </FlexibleGrid>
      </MakerSectionWrapper>
    );
  }

  return <></>;
};

type CategoryAreaProps = {
  maker: MakerIndexResponse;
  align: "left" | "center";
};

const CategoryArea = ({ maker, align }: CategoryAreaProps): JSX.Element => {
  const { isOpen, openHandler, closeHandler } = useDisclosure();
  const { openIfNotDesigner } = useModalLimitFunction();
  const { executeAction, SignInModalComponent } = useSignInRequiredAction({
    action() {
      openIfNotDesigner({ onDesigner: openHandler });
    },
    message: getReasonText(RedirectReason.ContactMaker),
  });
  const productURL = useMemo<string>(() => {
    if (
      maker.building_material_product_variations_count >=
      maker.interior_product_variations_count
    ) {
      return `/makers/${maker.id}/building_materials`;
    }

    return `/makers/${maker.id}/interiors`;
  }, [maker]);

  return (
    <>
      <div className="space-y-24" style={{ textAlign: align }}>
        <div
          className={[
            "flex flex-row items-center gap-8",
            align === "center" ? "justify-center" : "",
          ].join(" ")}
        >
          {maker.can_service_contacted && (
            <button
              className="block bg-yellow-500 p-12 rounded-full text-sm font-bold text-center max-w-[640px]"
              type="button"
              onClick={executeAction}
            >
              お問い合わせ
            </button>
          )}
          <Link
            href={productURL}
            className="block bg-gray-200 p-12 rounded-full text-sm font-bold text-center max-w-[640px]"
          >
            プロダクト一覧へ
          </Link>
        </div>

        <div className="space-y-8">
          <p className="font-bold text-sm">カテゴリ</p>
          <div
            className="flex flex-wrap gap-4 text-sm "
            style={{
              alignItems: align === "left" ? "flex-start" : "center",
              justifyContent: align === "left" ? "flex-start" : "center",
            }}
          >
            {maker.categories.map(({ name, id, kind }) => (
              <Link
                key={id}
                className="bg-white rounded-full px-12 py-8 hover:bg-black hover:text-white transition-all"
                href={`/makers/${maker.id}/${
                  kind === ProductKind.BUILDING_MATERIAL
                    ? "building_materials"
                    : "interiors"
                }?category_id=${id}`}
              >
                {name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <MakerContactModal
        isOpen={isOpen}
        closeHandler={closeHandler}
        maker={maker}
      />

      {SignInModalComponent}
    </>
  );
};
